import { Link } from '@tanstack/react-router'

import { Button } from '#/shared/ui/button'

import { usePasskeyAutofill } from './passkey-autofill'
import {
  passkeyCancelled,
  passkeyErrorMessage,
  passkeysSupported,
  type usePasskeySignIn,
} from './passkeys'

function shown(error: unknown): string | null {
  return error && !passkeyCancelled(error) ? passkeyErrorMessage(error) : null
}

/**
 * The second way in on the sign-in card.
 *
 * No email is asked: the browser offers whichever passkeys it holds for this
 * console — in the email field's autofill list while the card is idle, or in
 * its own prompt from the button — and the admin picks one with their
 * fingerprint, face or PIN. Closing that prompt is not an error, so nothing is
 * shown for it. Browsers without WebAuthn never see any of it.
 */
export function PasskeySignIn({
  passkey,
  busy,
  remember,
  onPasskey,
  onSignedIn,
}: {
  passkey: ReturnType<typeof usePasskeySignIn>
  busy: boolean
  remember: () => boolean
  onPasskey: () => void
  onSignedIn: () => void
}) {
  const supported = passkeysSupported()
  const refused = usePasskeyAutofill({
    enabled: supported && !busy && !passkey.isPending,
    remember,
    onSignedIn,
  })

  if (!supported) return null

  const problem = (passkey.isError ? shown(passkey.error) : null) ?? shown(refused)

  return (
    <div className="-mt-3 flex flex-col gap-3">
      <div className="text-label flex items-center gap-3 text-gray-400">
        <span className="h-px flex-1 bg-gray-200" />
        or
        <span className="h-px flex-1 bg-gray-200" />
      </div>
      {problem ? (
        <p role="alert" className="rounded-md bg-red-50 px-3 py-2 text-xs text-red-500">
          {problem}
        </p>
      ) : null}
      <Button
        type="button"
        size="lg"
        variant="outline"
        loading={passkey.isPending}
        disabled={busy}
        className="w-full text-gray-900"
        onClick={onPasskey}
      >
        Sign in with a passkey
      </Button>
      <Link
        to="/login/recovery"
        className="text-label text-center text-gray-500 hover:text-gray-900"
      >
        Lost your passkeys? Use a recovery code
      </Link>
    </div>
  )
}
